/**
 * `<RequireRole>` (W21-F1-S6, loading-state extended W127-F3).
 *
 * Renders children only if the current user has any of the listed Senviro
 * roles, otherwise renders the optional `fallback` (or nothing). Mirrors
 * legacy CakePHP `if ($this->Authorization->can(...))` blocks in admin
 * templates.
 *
 * W127-F3 — added explicit `loading` slot distinct from `fallback`. Pre-
 * W127-F3 the component returned `null` while membership data loaded; on
 * a slow network that meant the user briefly saw nothing → then a
 * permission-denied state if the role check resolved to false → then
 * real content if it resolved to true. The W127-F1 bootstrap usually
 * makes this resolve before first paint, but on cold-cache or refetch
 * it can still flash. Callers that care can pass `loading={<Spinner />}`
 * to render an explicit pending state.
 */
import * as React from 'react';
import { useAuthz, type SenviroRole } from './useAuthz';

export interface RequireRoleProps {
  roles: SenviroRole[];
  /** Element rendered when role check FAILS. Defaults to `null`. */
  fallback?: React.ReactNode;
  /**
   * Element rendered while the membership query is pending. Defaults
   * to `null` (matches pre-W127-F3 behavior for callers that don't
   * opt in). Pass an explicit spinner / "Access loading…" placeholder
   * for surfaces where the deny-flash is jarring.
   */
  loading?: React.ReactNode;
  children: React.ReactNode;
}

export function RequireRole({
  roles,
  fallback = null,
  loading = null,
  children,
}: RequireRoleProps): React.ReactElement | null {
  const { hasRole, isLoading } = useAuthz();
  if (isLoading) return <>{loading}</>;
  const granted = roles.some((r) => hasRole(r));
  if (!granted) return <>{fallback}</>;
  return <>{children}</>;
}
